import { createContext, useContext, useState, useEffect, useCallback, useRef, ReactNode } from 'react';
import {
  User,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  updateProfile,
  UserCredential,
  EmailAuthProvider,
  reauthenticateWithCredential,
  updatePassword as firebaseUpdatePassword,
} from 'firebase/auth';
import { auth } from '@core/config/firebase';
import { getDefaultGraphqlClient } from '@core/api/graphqlClient';
import { apolloContextForShop, apolloContextUnscopedShopList, type HasuraApolloContext } from '@core/api/apolloContext';
import { gql } from '@apollo/client';
import { useApolloClients } from '@core/context/ApolloClientsContext';
import { MULTI_SHOP_ENABLED } from '@core/config/shops';
import {
  fetchMstUserPlatformRegistryByMobile,
  fetchMstUserRowByMobileForShop,
  normalizeAuthEmail,
} from '../graphql/userRows';
import {
  isGeneratedFirebaseEmail,
  maskPhone,
  normalizePhoneIdentity,
  storedPhoneFromGeneratedFirebaseEmail,
} from '@shared/utils/phoneIdentity';

export interface UserData {
  id: string;
  name: string;
  email?: string | null;
  mobile: string;
  /** Tenant scope; platform registry rows use **`null`** (multi-shop identity anchor without a seller FK). Not a Hasura/console role flag. */
  shop_id: string | null;
  user_type: string;
  created_at: string;
  updated_at: string;
}

interface InsertUserResponse {
  insert_mst_users_one: UserData;
}

interface UpdateUserResponse {
  update_mst_users_by_pk: Partial<UserData>;
}

export interface SignUpOptions {
  /**
   * Creates **`mst_users`** rows **only** for these shops (`user_type`: customer).
   * When omitted: **multi-shop** → one optional **`shop_id: null`** registry row **still as customer**
   * (Firebase identity anchor); **single-shop** → one row under the sole tenant **`shop_id`**.
   */
  shopIds?: string[];
}

interface AuthContextType {
  user: User | null;
  userData: UserData | null;
  /** Per-shop user row keyed by **`shop_id`** (UUID); sparse map when multi-shop lazy enrollment is used. */
  userDataByShop: Record<string, UserData | null>;
  getUserForShop: (shopId: string) => UserData | null;
  loading: boolean;
  signUp: (
    email: string,
    password: string,
    name: string,
    phone: string,
    options?: SignUpOptions
  ) => Promise<UserCredential>;
  signIn: (phone: string, password: string) => Promise<UserCredential>;
  logout: () => Promise<void>;
  refreshUserData: () => Promise<void>;
  /** Lazily loads or inserts `mst_users` for `shopId`; updates cache + context. Idempotent per shop. */
  ensureCustomerForShop: (shopId: string) => Promise<UserData | null>;
  updateUserData: (id: string, name: string, email: string) => Promise<boolean>;
  changePassword: (currentPassword: string, newPassword: string) => Promise<{ success: boolean; message: string }>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

const INSERT_USER = gql`
  mutation InsertUser($user: mst_users_insert_input!) {
    insert_mst_users_one(object: $user) {
      id
      name
      email
      mobile
      shop_id
      user_type
      created_at
      updated_at
    }
  }
`;

const UPDATE_USER = gql`
  mutation UpdateUser($id: uuid!, $name: String!, $email: String!) {
    update_mst_users_by_pk(
      pk_columns: { id: $id }
      _set: { name: $name, email: $email }
    ) {
      id
      name
      email
      mobile
      updated_at
    }
  }
`;

/** Hasura `mst_users.user_type` value for every row this storefront creates (never staff / super-admin). */
const STOREFRONT_SIGNUP_USER_TYPE = 'customer';

const SET_USER_TYPE_CUSTOMER = gql`
  mutation SetUserTypeCustomer($id: uuid!, $userType: String!) {
    update_mst_users_by_pk(
      pk_columns: { id: $id }
      _set: { user_type: $userType }
    ) {
      id
      name
      email
      mobile
      shop_id
      user_type
      created_at
      updated_at
    }
  }
`;

function isStorefrontCustomerUserType(userType: string | undefined | null): boolean {
  return String(userType ?? '').trim().toLowerCase() === STOREFRONT_SIGNUP_USER_TYPE;
}

/**
 * DB triggers/presets sometimes overwrite `user_type` on insert. Only use for **`insert_mst_users_one` responses**
 * from this SPA — never for arbitrary fetched users (could be legitimate staff).
 */
async function coerceInsertedUserToStorefrontCustomer(
  row: UserData | null
): Promise<UserData | null> {
  if (!row) return null;
  if (isStorefrontCustomerUserType(row.user_type)) return row;

  const client = getDefaultGraphqlClient();
  const context = row.shop_id
    ? apolloContextForShop(row.shop_id)
    : apolloContextUnscopedShopList();

  try {
    const result = (await client.mutate({
      mutation: SET_USER_TYPE_CUSTOMER,
      variables: { id: row.id, userType: STOREFRONT_SIGNUP_USER_TYPE },
      context,
    })) as { data?: { update_mst_users_by_pk: UserData | null } };

    const fixed = result.data?.update_mst_users_by_pk;
    if (fixed) {
      console.warn(
        '[auth] Coerced storefront insert to customer — check DB defaults/triggers on mst_users.user_type',
        { id: row.id, previousType: row.user_type }
      );
      return fixed;
    }
  } catch (e) {
    console.error('[auth] coerceInsertedUserToStorefrontCustomer failed:', e);
  }

  return row;
}

function enrollmentStorageKey(uid: string): string {
  return `customer_enrollment:${uid}`;
}

type EnrollmentBlob = {
  shops: Record<string, UserData>;
  platform?: UserData | null;
};

function readEnrollmentBlob(uid: string): EnrollmentBlob {
  try {
    const raw = localStorage.getItem(enrollmentStorageKey(uid));
    if (!raw) return { shops: {} };
    const parsed = JSON.parse(raw) as unknown;
    if (!parsed || typeof parsed !== 'object') return { shops: {} };
    const o = parsed as Record<string, unknown>;
    if ('shops' in o && o.shops && typeof o.shops === 'object' && !Array.isArray(o.shops)) {
      return {
        shops: o.shops as Record<string, UserData>,
        platform: (o.platform as UserData | undefined) ?? undefined,
      };
    }
    return { shops: parsed as Record<string, UserData> };
  } catch {
    return { shops: {} };
  }
}

function writeEnrollmentBlob(uid: string, blob: EnrollmentBlob): void {
  const compactShops: Record<string, UserData> = {};
  for (const [k, v] of Object.entries(blob.shops)) {
    if (v) compactShops[k] = v;
  }
  const payload: EnrollmentBlob = { shops: compactShops };
  if (blob.platform) payload.platform = blob.platform;
  try {
    localStorage.setItem(enrollmentStorageKey(uid), JSON.stringify(payload));
  } catch {
    /* ignore quota */
  }
}

function clearEnrollmentBlob(uid: string): void {
  try {
    localStorage.removeItem(enrollmentStorageKey(uid));
  } catch {
    /* ignore */
  }
}

function contactEmailStorageKey(uid: string): string {
  return `contact_email:${uid}`;
}

function readPersistedContactEmail(uid: string): string {
  try {
    return normalizeContactEmailForDisplay(
      localStorage.getItem(contactEmailStorageKey(uid))
    );
  } catch {
    return '';
  }
}

function writePersistedContactEmail(uid: string, contactEmail: string): void {
  const normalized = normalizeContactEmailForDisplay(contactEmail);
  if (!uid || !normalized) return;
  try {
    localStorage.setItem(contactEmailStorageKey(uid), normalized);
  } catch {
    /* ignore quota */
  }
}

function clearPersistedContactEmail(uid: string): void {
  try {
    localStorage.removeItem(contactEmailStorageKey(uid));
  } catch {
    /* ignore */
  }
}

function normalizeContactEmailForDisplay(email: string | null | undefined): string {
  const normalized = normalizeAuthEmail(email);
  return normalized && !isGeneratedFirebaseEmail(normalized) ? normalized : '';
}

function bestContactEmailFromSources(
  ...sources: Array<UserData | string | null | undefined>
): string {
  for (const source of sources) {
    const contactEmail =
      typeof source === 'string'
        ? normalizeContactEmailForDisplay(source)
        : normalizeContactEmailForDisplay(source?.email);
    if (contactEmail) return contactEmail;
  }
  return '';
}

function withContactEmailFallback(
  row: UserData | null,
  contactEmail: string
): UserData | null {
  if (!row || !contactEmail || normalizeContactEmailForDisplay(row.email)) {
    return row;
  }
  return { ...row, email: contactEmail };
}

function mergeContactEmailIntoShops(
  rowsByShop: Record<string, UserData | null>,
  contactEmail: string
): Record<string, UserData | null> {
  if (!contactEmail) return rowsByShop;
  return Object.fromEntries(
    Object.entries(rowsByShop).map(([shopId, row]) => [
      shopId,
      withContactEmailFallback(row, contactEmail),
    ])
  );
}

function compactEnrollmentShops(
  rowsByShop: Record<string, UserData | null>
): Record<string, UserData> {
  const compact: Record<string, UserData> = {};
  for (const [shopId, row] of Object.entries(rowsByShop)) {
    if (row) compact[shopId] = row;
  }
  return compact;
}

function selectPrimaryUserData(
  hydratedShops: Record<string, UserData | null>,
  platformRowWithEmail: UserData | null,
  firstShopId?: string
): UserData | null {
  const rowWithContactEmail = Object.values(hydratedShops).find((row) =>
    normalizeContactEmailForDisplay(row?.email)
  );
  if (rowWithContactEmail) return rowWithContactEmail;

  if (
    platformRowWithEmail &&
    normalizeContactEmailForDisplay(platformRowWithEmail.email)
  ) {
    return platformRowWithEmail;
  }

  return (
    (firstShopId ? hydratedShops[firstShopId] : null) ??
    Object.values(hydratedShops).find(Boolean) ??
    platformRowWithEmail ??
    null
  );
}

async function verifyInsertedContactEmail(
  row: UserData | null,
  expectedEmail: string,
  refetch: () => Promise<UserData | null>
): Promise<UserData | null> {
  const expected = normalizeContactEmailForDisplay(expectedEmail);
  if (!row || !expected) return row;

  if (normalizeContactEmailForDisplay(row.email)) return row;

  const refetched = await refetch();
  if (refetched && normalizeContactEmailForDisplay(refetched.email)) {
    return refetched;
  }

  console.warn(
    '[auth] mst_users.email was not persisted after insert — check DB triggers/Hasura permissions',
    { id: row.id, shop_id: row.shop_id }
  );
  return withContactEmailFallback(row, expected);
}

function AuthProviderInner({ children }: AuthProviderProps) {
  const { shops } = useApolloClients();
  const [user, setUser] = useState<User | null>(null);
  const [userData, setUserData] = useState<UserData | null>(null);
  const [userDataByShop, setUserDataByShop] = useState<
    Record<string, UserData | null>
  >({});

  const [loading, setLoading] = useState<boolean>(true);

  const enrollmentLocksRef = useRef<Map<string, Promise<UserData | null>>>(new Map());
  const pendingContactEmailRef = useRef<Record<string, string>>({});
  const fetchGenerationRef = useRef(0);
  const signUpInProgressRef = useRef(false);
  const userDataRef = useRef(userData);
  const userDataByShopRef = useRef(userDataByShop);
  userDataRef.current = userData;
  userDataByShopRef.current = userDataByShop;

  const rememberContactEmail = useCallback(
    (key: string | null | undefined, contactEmail: string): void => {
      const normalizedKey = String(key ?? '').trim().toLowerCase();
      const normalizedEmail = normalizeContactEmailForDisplay(contactEmail);
      if (!normalizedKey || !normalizedEmail) return;
      pendingContactEmailRef.current[normalizedKey] = normalizedEmail;
    },
    []
  );

  const pendingContactEmailFor = useCallback(
    (...keys: Array<string | null | undefined>): string => {
      for (const key of keys) {
        const normalizedKey = String(key ?? '').trim().toLowerCase();
        const contactEmail = normalizedKey
          ? pendingContactEmailRef.current[normalizedKey]
          : '';
        if (contactEmail) return contactEmail;
      }
      return '';
    },
    []
  );

  const getUserForShop = useCallback(
    (shopId: string) => userDataByShop[shopId] ?? null,
    [userDataByShop]
  );

  const fetchUserRowForShop = useCallback(
    async (
      mobile: string,
      shopId: string,
      legacyGeneratedEmail?: string
    ): Promise<UserData | null> => {
      const row = await fetchMstUserRowByMobileForShop(
        mobile,
        shopId,
        legacyGeneratedEmail
      );
      return (row as UserData | null) ?? null;
    },
    []
  );

  const insertUserRowForShop = useCallback(
    async (
      mobile: string,
      name: string,
      contactEmail: string,
      shopId: string,
      legacyGeneratedEmail?: string
    ): Promise<UserData | null> => {
      const shop = shops.find((s) => s.id === shopId);
      if (!shop) return null;

      const storedMobile = mobile.trim();
      const canonContactEmail = normalizeAuthEmail(contactEmail);
      const userInput: Record<string, unknown> = {
        name: name || 'User',
        email: canonContactEmail || '',
        mobile: storedMobile,
        shop_id: shop.id,
        user_type: STOREFRONT_SIGNUP_USER_TYPE,
      };

      try {
        const client = getDefaultGraphqlClient();
        const result = (await client.mutate({
          mutation: INSERT_USER,
          variables: { user: userInput },
          context: apolloContextForShop(shop.id),
        })) as { data: InsertUserResponse };
        const inserted = await coerceInsertedUserToStorefrontCustomer(
          (result.data?.insert_mst_users_one as UserData) ?? null
        );
        return verifyInsertedContactEmail(
          withContactEmailFallback(inserted, canonContactEmail),
          canonContactEmail,
          () => fetchUserRowForShop(storedMobile, shop.id, legacyGeneratedEmail)
        );
      } catch (error: unknown) {
        const err = error as { graphQLErrors?: { message?: string }[] };
        console.error(`Error creating user in database (${shop.id}):`, error);
        if (err.graphQLErrors?.some((e) => e.message?.toLowerCase().includes('mobile'))) {
          return withContactEmailFallback(
            await fetchUserRowForShop(storedMobile, shop.id, legacyGeneratedEmail),
            canonContactEmail
          );
        }
        return null;
      }
    },
    [fetchUserRowForShop, shops]
  );

  const fetchPlatformUserByMobile = useCallback(
    async (
      mobile: string,
      legacyGeneratedEmail?: string
    ): Promise<UserData | null> => {
      const row = await fetchMstUserPlatformRegistryByMobile(
        mobile,
        legacyGeneratedEmail
      );
      return (row as UserData | null) ?? null;
    },
    []
  );

  const insertPlatformCustomerRow = useCallback(
    async (
      mobile: string,
      name: string,
      contactEmail: string,
      legacyGeneratedEmail?: string
    ): Promise<UserData | null> => {
      const client = getDefaultGraphqlClient();
      const storedMobile = mobile.trim();
      const canonContactEmail = normalizeAuthEmail(contactEmail);
      const userInput: Record<string, unknown> = {
        name: name || 'User',
        email: canonContactEmail || '',
        mobile: storedMobile,
        shop_id: null,
        user_type: STOREFRONT_SIGNUP_USER_TYPE,
      };

      try {
        const result = (await client.mutate({
          mutation: INSERT_USER,
          variables: { user: userInput },
          context: apolloContextUnscopedShopList(),
        })) as { data: InsertUserResponse };
        const inserted = await coerceInsertedUserToStorefrontCustomer(
          (result.data?.insert_mst_users_one as UserData) ?? null
        );
        return verifyInsertedContactEmail(
          withContactEmailFallback(inserted, canonContactEmail),
          canonContactEmail,
          () => fetchPlatformUserByMobile(storedMobile, legacyGeneratedEmail)
        );
      } catch (error: unknown) {
        console.warn('[auth] Platform insert (nullable shop_id) failed:', error);
        const fallbackShop =
          typeof import.meta.env.VITE_PLATFORM_REGISTRY_SHOP_ID === 'string'
            ? import.meta.env.VITE_PLATFORM_REGISTRY_SHOP_ID.trim()
            : '';
        if (fallbackShop && shops.some((s) => s.id === fallbackShop)) {
          console.warn(
            '[auth] Using VITE_PLATFORM_REGISTRY_SHOP_ID fallback — DB may require shop_id.'
          );
          return insertUserRowForShop(
            storedMobile,
            name,
            contactEmail,
            fallbackShop,
            legacyGeneratedEmail
          );
        }
        const err = error as { graphQLErrors?: { message?: string }[] };
        if (err.graphQLErrors?.some((e) => e.message?.toLowerCase().includes('mobile'))) {
          return withContactEmailFallback(
            await fetchPlatformUserByMobile(storedMobile, legacyGeneratedEmail),
            canonContactEmail
          );
        }
        return null;
      }
    },
    [shops, insertUserRowForShop, fetchPlatformUserByMobile]
  );

  const fetchUserData = useCallback(
    async (generatedEmail: string, firebaseUid: string): Promise<void> => {
      if (signUpInProgressRef.current) return;

      const fetchGeneration = ++fetchGenerationRef.current;
      const isStaleFetch = (): boolean =>
        fetchGeneration !== fetchGenerationRef.current;

      const normalizedGeneratedEmail = normalizeAuthEmail(generatedEmail);
      const storedMobile = storedPhoneFromGeneratedFirebaseEmail(
        normalizedGeneratedEmail
      );
      const rememberedContactEmail = pendingContactEmailFor(
        firebaseUid,
        normalizedGeneratedEmail
      );
      const persistedContactEmail = readPersistedContactEmail(firebaseUid);

      if (!storedMobile) {
        if (isStaleFetch()) return;
        setUserData(null);
        setUserDataByShop({});
        return;
      }
      try {
        const blob = readEnrollmentBlob(firebaseUid);
        const platformRowFetched = await fetchPlatformUserByMobile(
          storedMobile,
          normalizedGeneratedEmail
        );
        if (isStaleFetch()) return;

        const platformRow = platformRowFetched ?? blob.platform ?? null;

        if (!MULTI_SHOP_ENABLED) {
          const shop = shops[0];
          if (!shop) {
            const contactEmail = bestContactEmailFromSources(
              platformRow,
              blob.platform,
              ...Object.values(blob.shops),
              userDataRef.current,
              persistedContactEmail,
              rememberedContactEmail
            );
            const platformRowWithEmail = withContactEmailFallback(
              platformRow,
              contactEmail
            );
            if (isStaleFetch()) return;
            setUserData(platformRowWithEmail);
            setUserDataByShop({});
            writeEnrollmentBlob(firebaseUid, {
              shops: {},
              platform: platformRowWithEmail ?? undefined,
            });
            return;
          }

          const tenantRowFetched = await fetchUserRowForShop(
            storedMobile,
            shop.id,
            normalizedGeneratedEmail
          );
          if (isStaleFetch()) return;

          const tenantRow =
            (tenantRowFetched as UserData | null) ??
            blob.shops[shop.id] ??
            null;
          const contactEmail = bestContactEmailFromSources(
            tenantRowFetched as UserData | null,
            blob.shops[shop.id],
            platformRow,
            blob.platform,
            ...Object.values(blob.shops),
            userDataRef.current,
            persistedContactEmail,
            rememberedContactEmail
          );
          const tenantRowWithEmail = withContactEmailFallback(
            tenantRow,
            contactEmail
          );
          const platformRowWithEmail = withContactEmailFallback(
            platformRow,
            contactEmail
          );

          const nextShops = { ...blob.shops };
          if (tenantRowWithEmail) nextShops[shop.id] = tenantRowWithEmail;
          const hydratedShops = mergeContactEmailIntoShops(
            nextShops,
            contactEmail
          );

          writeEnrollmentBlob(firebaseUid, {
            shops: compactEnrollmentShops(hydratedShops),
            platform: platformRowWithEmail ?? undefined,
          });

          if (isStaleFetch()) return;

          setUserDataByShop(
            tenantRowWithEmail ? { [shop.id]: tenantRowWithEmail } : {}
          );
          setUserData(
            selectPrimaryUserData(
              hydratedShops,
              platformRowWithEmail,
              shop.id
            )
          );

          if (!tenantRowWithEmail && !platformRowWithEmail) {
            console.warn('User not found in DB for mobile identity:', {
              mobile: maskPhone(storedMobile),
            });
          }
          return;
        }

        const mergedShops: Record<string, UserData | null> = { ...blob.shops };
        await Promise.allSettled(
          shops.map(async s => {
            const hit = await fetchUserRowForShop(
              storedMobile,
              s.id,
              normalizedGeneratedEmail
            );
            if (hit) mergedShops[s.id] = hit as UserData;
          })
        );
        if (isStaleFetch()) return;

        const contactEmail = bestContactEmailFromSources(
          platformRow,
          blob.platform,
          ...Object.values(mergedShops),
          ...Object.values(blob.shops),
          userDataRef.current,
          persistedContactEmail,
          rememberedContactEmail
        );
        const hydratedShops = mergeContactEmailIntoShops(
          mergedShops,
          contactEmail
        );
        const platformRowWithEmail = withContactEmailFallback(
          platformRow,
          contactEmail
        );
        const primary = selectPrimaryUserData(
          hydratedShops,
          platformRowWithEmail,
          shops[0]?.id
        );

        writeEnrollmentBlob(firebaseUid, {
          shops: compactEnrollmentShops(hydratedShops),
          platform: platformRowWithEmail ?? undefined,
        });

        if (isStaleFetch()) return;

        setUserDataByShop(hydratedShops);
        setUserData(primary);
      } catch (error) {
        console.error('Error fetching user data:', error);
        if (isStaleFetch()) return;
        setUserData(null);
        setUserDataByShop({});
      }
    },
    [shops, fetchPlatformUserByMobile, fetchUserRowForShop, pendingContactEmailFor]
  );

  const createUserInDatabase = async (
    mobile: string,
    name: string,
    contactEmail: string,
    targetShopIds: string[],
    legacyGeneratedEmail?: string
  ): Promise<{ map: Record<string, UserData | null>; primary: UserData | null }> => {
    const allowed = new Set(shops.map((s) => s.id));
    const uniqueTargets = [...new Set(targetShopIds.filter((id) => allowed.has(id)))];
    const map: Record<string, UserData | null> = {};
    let primary: UserData | null = null;

    const shopsToProvision = shops.filter((s) => uniqueTargets.includes(s.id));

    await Promise.all(
      shopsToProvision.map(async (shop) => {
        const row = await insertUserRowForShop(
          mobile,
          name,
          contactEmail,
          shop.id,
          legacyGeneratedEmail
        );
        map[shop.id] = row;
        if (!primary && row) primary = row;
      })
    );

    return { map, primary };
  };

  const ensureCustomerForShop = useCallback(
    async (shopId: string): Promise<UserData | null> => {
      const generatedEmail = normalizeAuthEmail(user?.email);
      const firebaseUid = user?.uid;
      const storedMobile = storedPhoneFromGeneratedFirebaseEmail(generatedEmail);
      if (!generatedEmail || !storedMobile || !firebaseUid) return null;

      const allowed = shops.some((s) => s.id === shopId);
      if (!allowed) return null;

      const pending = enrollmentLocksRef.current.get(shopId);
      if (pending) return pending;

      const task = (async (): Promise<UserData | null> => {
        const blob = readEnrollmentBlob(firebaseUid);
        const persistedContactEmail = readPersistedContactEmail(firebaseUid);
        const rememberedContactEmail = pendingContactEmailFor(
          firebaseUid,
          generatedEmail
        );
        const knownContactEmail = bestContactEmailFromSources(
          userDataRef.current,
          blob.platform,
          ...Object.values(blob.shops),
          persistedContactEmail,
          rememberedContactEmail
        );
        const cachedRow = userDataByShopRef.current[shopId];
        if (cachedRow) {
          const hydratedCachedRow = withContactEmailFallback(
            cachedRow,
            knownContactEmail
          );
          if (hydratedCachedRow !== cachedRow) {
            setUserDataByShop((prev) => ({
              ...prev,
              [shopId]: hydratedCachedRow,
            }));
            if (
              hydratedCachedRow &&
              normalizeContactEmailForDisplay(hydratedCachedRow.email)
            ) {
              setUserData((prev) =>
                selectPrimaryUserData(
                  { ...userDataByShopRef.current, [shopId]: hydratedCachedRow },
                  blob.platform ?? null,
                  shops[0]?.id
                ) ?? prev
              );
            }
          }
          return hydratedCachedRow;
        }

        let row = await fetchUserRowForShop(storedMobile, shopId, generatedEmail);
        if (row) {
          const hydratedRow = withContactEmailFallback(row, knownContactEmail) ?? row;
          row = hydratedRow;
          setUserDataByShop((prev) => ({ ...prev, [shopId]: hydratedRow }));
          writeEnrollmentBlob(firebaseUid, {
            shops: { ...blob.shops, [shopId]: hydratedRow },
            platform: blob.platform,
          });
          setUserData((prev) =>
            selectPrimaryUserData(
              { ...userDataByShopRef.current, [shopId]: hydratedRow },
              withContactEmailFallback(blob.platform ?? null, knownContactEmail),
              shops[0]?.id
            ) ?? prev ?? hydratedRow
          );
          return hydratedRow;
        }

        const platform = await fetchPlatformUserByMobile(storedMobile, generatedEmail);
        const contactEmail = bestContactEmailFromSources(
          platform,
          blob.platform,
          ...Object.values(blob.shops),
          userDataRef.current,
          persistedContactEmail,
          rememberedContactEmail
        );
        const name =
          user?.displayName?.trim() ||
          platform?.name?.trim() ||
          blob.platform?.name?.trim() ||
          'User';

        row = await insertUserRowForShop(
          storedMobile,
          name,
          contactEmail,
          shopId,
          generatedEmail
        );
        if (!row) {
          row = await fetchUserRowForShop(storedMobile, shopId, generatedEmail);
          row = withContactEmailFallback(row, contactEmail);
        }

        if (row) {
          setUserDataByShop((prev) => ({ ...prev, [shopId]: row }));
          writeEnrollmentBlob(firebaseUid, {
            shops: { ...blob.shops, [shopId]: row },
            platform: withContactEmailFallback(blob.platform ?? null, contactEmail) ?? blob.platform,
          });
          setUserData((prev) =>
            selectPrimaryUserData(
              { ...userDataByShopRef.current, [shopId]: row },
              withContactEmailFallback(blob.platform ?? null, contactEmail),
              shops[0]?.id
            ) ?? prev ?? row
          );
        }

        return row;
      })();

      enrollmentLocksRef.current.set(shopId, task);
      try {
        return await task;
      } finally {
        enrollmentLocksRef.current.delete(shopId);
      }
    },
    [
      user?.email,
      user?.uid,
      user?.displayName,
      shops,
      fetchUserRowForShop,
      insertUserRowForShop,
      fetchPlatformUserByMobile,
      pendingContactEmailFor,
    ]
  );

  const signUp = async (
    email: string,
    password: string,
    name: string,
    phone: string,
    options?: SignUpOptions
  ): Promise<UserCredential> => {
    const allowed = new Set(shops.map((s) => s.id));
    const targetShopIds =
      options?.shopIds?.filter((id) => allowed.has(id)) ?? [];

    const contactEmail = normalizeAuthEmail(email);
    if (!contactEmail) {
      throw new Error('Please enter a valid email address.');
    }
    const phoneIdentity = normalizePhoneIdentity(phone);
    rememberContactEmail(phoneIdentity.generatedEmail, contactEmail);

    fetchGenerationRef.current += 1;
    signUpInProgressRef.current = true;

    let userCredential: UserCredential;
    try {
      userCredential = await createUserWithEmailAndPassword(
        auth,
        phoneIdentity.generatedEmail,
        password
      );
      await updateProfile(userCredential.user, { displayName: name });

      const generatedEmail =
        normalizeAuthEmail(userCredential.user.email) || phoneIdentity.generatedEmail;
      const uid = userCredential.user.uid;
      rememberContactEmail(uid, contactEmail);
      rememberContactEmail(generatedEmail, contactEmail);
      writePersistedContactEmail(uid, contactEmail);

      if (targetShopIds.length === 0) {
        const prevBlob = readEnrollmentBlob(uid);

        /** Single storefront: anchor the shopper on the lone tenant UUID — avoids a global `shop_id: null` row that back-office screens often classify as privileged. */
        if (!MULTI_SHOP_ENABLED && shops[0]) {
          let tenantRow = await fetchUserRowForShop(
            phoneIdentity.storedPhone,
            shops[0].id,
            generatedEmail
          );
          tenantRow =
            tenantRow ??
            (await insertUserRowForShop(
              phoneIdentity.storedPhone,
              name,
              contactEmail,
              shops[0].id,
              generatedEmail
            ));
          const tenantRowWithEmail = withContactEmailFallback(
            tenantRow,
            contactEmail
          );

          const nextShops = { ...prevBlob.shops };
          if (tenantRowWithEmail) nextShops[shops[0].id] = tenantRowWithEmail;
          const hydratedShops = mergeContactEmailIntoShops(
            nextShops,
            contactEmail
          );

          writeEnrollmentBlob(uid, {
            shops: compactEnrollmentShops(hydratedShops),
            platform: prevBlob.platform,
          });
          setUserDataByShop(hydratedShops);
          setUserData(
            selectPrimaryUserData(
              hydratedShops,
              null,
              shops[0].id
            ) ?? tenantRowWithEmail
          );

          if (!tenantRowWithEmail) {
            console.error(
              '[auth] Single-tenant signup could not provision mst_users customer for shop',
              shops[0].id
            );
          }
          return userCredential;
        }

        let platformRow = await fetchPlatformUserByMobile(
          phoneIdentity.storedPhone,
          generatedEmail
        );
        platformRow =
          platformRow ??
          (await insertPlatformCustomerRow(
            phoneIdentity.storedPhone,
            name,
            contactEmail,
            generatedEmail
          ));
        const platformRowWithEmail = withContactEmailFallback(
          platformRow,
          contactEmail
        );
        const hydratedShops = mergeContactEmailIntoShops(
          prevBlob.shops,
          contactEmail
        );
        writeEnrollmentBlob(uid, {
          shops: compactEnrollmentShops(hydratedShops),
          platform: platformRowWithEmail ?? prevBlob.platform,
        });
        setUserDataByShop({ ...hydratedShops });
        setUserData(
          selectPrimaryUserData(
            hydratedShops,
            platformRowWithEmail,
            shops[0]?.id
          )
        );
        if (!platformRowWithEmail) {
          console.error(
            '[auth] Could not create platform mst_users row. If shop_id is NOT NULL in your DB, set VITE_PLATFORM_REGISTRY_SHOP_ID or allow NULL shop_id.'
          );
        }
        return userCredential;
      }

      const { map, primary } = await createUserInDatabase(
        phoneIdentity.storedPhone,
        name,
        contactEmail,
        targetShopIds,
        generatedEmail
      );
      const hydratedMap = mergeContactEmailIntoShops(map, contactEmail);
      const hydratedPrimary =
        withContactEmailFallback(primary, contactEmail) ??
        Object.values(hydratedMap).find((row) =>
          normalizeContactEmailForDisplay(row?.email)
        ) ??
        Object.values(hydratedMap).find(Boolean) ??
        null;
      const blob = readEnrollmentBlob(uid);
      const nextShops = { ...blob.shops };
      for (const [k, v] of Object.entries(hydratedMap)) {
        if (v) nextShops[k] = v;
      }
      writeEnrollmentBlob(uid, {
        shops: compactEnrollmentShops(nextShops),
        platform: withContactEmailFallback(blob.platform ?? null, contactEmail) ?? undefined,
      });
      setUserDataByShop(nextShops);
      if (hydratedPrimary) {
        setUserData(
          selectPrimaryUserData(nextShops, blob.platform ?? null, shops[0]?.id) ??
            hydratedPrimary
        );
      }
      return userCredential;
    } finally {
      signUpInProgressRef.current = false;
      const currentUser = auth.currentUser;
      if (currentUser?.email && currentUser.uid) {
        await fetchUserData(
          normalizeAuthEmail(currentUser.email),
          currentUser.uid
        );
      }
    }
  };

  const signIn = async (phone: string, password: string): Promise<UserCredential> => {
    const { generatedEmail } = normalizePhoneIdentity(phone);
    return signInWithEmailAndPassword(auth, generatedEmail, password);
  };

  const logout = async (): Promise<void> => {
    const uid = user?.uid;
    const generatedEmail = normalizeAuthEmail(user?.email);
    await signOut(auth);
    setUserData(null);
    setUserDataByShop({});
    if (uid) {
      clearEnrollmentBlob(uid);
      clearPersistedContactEmail(uid);
      delete pendingContactEmailRef.current[uid];
    }
    if (generatedEmail) {
      delete pendingContactEmailRef.current[generatedEmail];
    }
  };

  const refreshUserData = async (): Promise<void> => {
    if (user?.email && user?.uid) {
      await fetchUserData(normalizeAuthEmail(user.email), user.uid);
    }
  };

  const updateUserData = async (_id: string, name: string, contactEmail: string): Promise<boolean> => {
    try {
      const uid = user?.uid;
      const generatedEmail = user?.email;
      const blob = uid ? readEnrollmentBlob(uid) : { shops: {}, platform: undefined };

      const targets: { id: string; ctx: HasuraApolloContext }[] = [];

      if (blob.platform?.id) {
        targets.push({ id: blob.platform.id, ctx: apolloContextUnscopedShopList() });
      }

      if (!MULTI_SHOP_ENABLED) {
        const shop = shops[0];
        const ud = shop ? userDataByShop[shop.id] ?? userData : userData;
        if (ud?.id && ud.shop_id) {
          targets.push({ id: ud.id, ctx: apolloContextForShop(ud.shop_id) });
        }
      } else {
        for (const s of shops) {
          const ud = userDataByShop[s.id];
          if (ud?.id) targets.push({ id: ud.id, ctx: apolloContextForShop(s.id) });
        }
      }

      const seen = new Set<string>();
      let anyOk = false;
      const client = getDefaultGraphqlClient();

      for (const t of targets) {
        if (seen.has(t.id)) continue;
        seen.add(t.id);
        const result = (await client.mutate({
          mutation: UPDATE_USER,
          variables: { id: t.id, name, email: normalizeAuthEmail(contactEmail) },
          context: t.ctx,
        })) as { data: UpdateUserResponse };

        if (result.data?.update_mst_users_by_pk) {
          anyOk = true;
        }
      }

      if (anyOk && generatedEmail && uid) {
        writePersistedContactEmail(uid, contactEmail);
        await fetchUserData(normalizeAuthEmail(generatedEmail), uid);
      }

      return anyOk;
    } catch (error) {
      console.error('Error updating user data:', error);
      return false;
    }
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (firebaseUser) => {
      setUser(firebaseUser);

      if (!firebaseUser) {
        setUserData(null);
        setUserDataByShop({});
      }

      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  useEffect(() => {
    if (user?.email && user?.uid && shops.length) {
      void fetchUserData(normalizeAuthEmail(user.email), user.uid);
    }
  }, [user?.email, user?.uid, shops, fetchUserData]);

  return (
    <AuthContext.Provider
      value={{
        user,
        userData,
        userDataByShop,
        getUserForShop,
        loading,
        signUp,
        signIn,
        logout,
        refreshUserData,
        ensureCustomerForShop,
        updateUserData,
        changePassword: async (currentPassword, newPassword) => {
          if (!auth.currentUser) return { success: false, message: 'No authenticated user found.' };

          const currentUser = auth.currentUser;
          const email = currentUser.email;

          if (!email) {
            return {
              success: false,
              message:
                'This account cannot update its password right now. Please log out and sign in again, then try once more.',
            };
          }

          try {
            const credential = EmailAuthProvider.credential(email, currentPassword);
            await reauthenticateWithCredential(currentUser, credential);

            await firebaseUpdatePassword(currentUser, newPassword);

            return { success: true, message: 'Password updated successfully!' };
          } catch (error: unknown) {
            const err = error as { code?: string; message?: string };
            console.error('Password update error:', error);

            let message = 'Failed to update password. Please try again.';

            if (err.code === 'auth/wrong-password') {
              message = 'The current password you entered is incorrect.';
            } else if (err.code === 'auth/weak-password') {
              message = 'The new password is too weak. Please use at least 6 characters.';
            } else if (err.code === 'auth/requires-recent-login') {
              message =
                'For security reasons, please log out and log back in before changing your password.';
            } else if (err.code === 'auth/user-mismatch') {
              message =
                'Authentication mismatch. Please ensure you are logged into the correct account.';
            } else if (err.message) {
              message = err.message;
            }

            return { success: false, message };
          }
        },
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export const AuthProvider = ({ children }: AuthProviderProps) => (
  <AuthProviderInner>{children}</AuthProviderInner>
);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
